// 슬립모드 자동 방지 시스템
class AntiSleepSystem {
    constructor() {
        this.sharedWorker = null;
        this.wakeLock = null;
        this.heartbeatWorker = null;
        
        this.init();
    }
    
    async init() {
        // 1. SharedWorker로 WebSocket 연결 유지
        this.initSharedWorker();
        
        // 2. Web Worker로 백그라운드 작업
        this.initWebWorker();
        
        // 3. 사용자 상호작용 감지
        this.setupUserInteraction();
        
        // 4. 페이지 가시성 모니터링
        this.monitorPageVisibility();
        
        // 5. 네트워크 상태 감지
        this.monitorNetworkStatus();
    }
    
    initSharedWorker() {
        if ('SharedWorker' in window) {
            try {
                this.sharedWorker = new SharedWorker('/shared-worker.js');
                this.sharedWorker.port.start();
                
                this.sharedWorker.port.onmessage = (e) => {
                    if (e.data.type === 'new-request') {
                        this.handleNewRequest(e.data);
                    }
                };
                
                console.log('SharedWorker 초기화 완료');
            } catch (error) {
                console.error('SharedWorker 초기화 실패:', error);
                // 폴백: 일반 WebSocket 사용
                this.fallbackToDirectWebSocket();
            }
        }
    }
    
    initWebWorker() {
        // 인라인 워커 생성 (별도 파일 불필요)
        const workerCode = `
            let interval;
            self.addEventListener('message', (e) => {
                if (e.data.cmd === 'start') {
                    interval = setInterval(() => {
                        self.postMessage({ type: 'heartbeat', time: Date.now() });
                    }, 5000);
                } else if (e.data.cmd === 'stop') {
                    clearInterval(interval);
                }
            });
        `;
        
        const blob = new Blob([workerCode], { type: 'application/javascript' });
        this.heartbeatWorker = new Worker(URL.createObjectURL(blob));
        
        this.heartbeatWorker.onmessage = (e) => {
            if (e.data.type === 'heartbeat') {
                // 연결 상태 체크
                this.checkConnectionStatus();
            }
        };
        
        this.heartbeatWorker.postMessage({ cmd: 'start' });
    }
    
    async setupUserInteraction() {
        // 첫 상호작용 시 모든 권한 요청
        document.addEventListener('click', async () => {
            // 1. 알림 권한
            if ('Notification' in window && Notification.permission === 'default') {
                await Notification.requestPermission();
            }
            
            // 2. Wake Lock
            if ('wakeLock' in navigator && !this.wakeLock) {
                try {
                    this.wakeLock = await navigator.wakeLock.request('screen');
                    console.log('Wake Lock 활성화됨');
                } catch (err) {
                    console.log('Wake Lock 실패:', err);
                }
            }
        }, { once: true });
    }
    
    monitorPageVisibility() {
        let hiddenTime = null;
        
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                hiddenTime = Date.now();
                console.log('페이지가 백그라운드로 전환됨');
                
                // SharedWorker에 상태 알림
                if (this.sharedWorker) {
                    this.sharedWorker.port.postMessage({ 
                        type: 'visibility-change', 
                        hidden: true 
                    });
                }
            } else {
                if (hiddenTime) {
                    const duration = Date.now() - hiddenTime;
                    console.log(`페이지가 ${duration}ms 동안 비활성 상태였음`);
                    
                    // 놓친 데이터 동기화
                    this.syncMissedData(hiddenTime);
                }
                
                // Wake Lock 재활성화
                if (this.wakeLock === null && 'wakeLock' in navigator) {
                    navigator.wakeLock.request('screen').then(lock => {
                        this.wakeLock = lock;
                    }).catch(() => {});
                }
            }
        });
    }
    
    monitorNetworkStatus() {
        window.addEventListener('online', () => {
            console.log('네트워크 연결됨');
            this.reconnectAll();
        });
        
        window.addEventListener('offline', () => {
            console.log('네트워크 연결 끊김');
        });
    }
    
    async syncMissedData(since) {
        try {
            const response = await fetch(`/api/missed-requests?since=${since}`);
            
            // 응답이 JSON인지 확인
            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                console.warn('API 응답이 JSON이 아닙니다:', contentType);
                return;
            }
            
            if (!response.ok) {
                console.error('API 요청 실패:', response.status, response.statusText);
                return;
            }
            
            const data = await response.json();
            
            if (data.requests && data.requests.length > 0) {
                data.requests.forEach(request => {
                    this.handleNewRequest(request);
                });
            }
        } catch (error) {
            console.error('데이터 동기화 실패:', error);
        }
    }
    
    handleNewRequest(data) {
        // 알림 표시 (권한이 있는 경우만)
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('새로운 요청', {
                body: data.message,
                requireInteraction: true
            });
        }
        
        // 사운드 재생
        const audio = new Audio(data.soundUrl);
        audio.play().catch(() => {
            // 자동 재생 실패 시 사용자 상호작용 대기
            document.addEventListener('click', () => {
                audio.play();
            }, { once: true });
        });
        
        // 팝업 열기
        if (data.popupType) {
            window.open(`/popup?type=${data.popupType}`, '_blank', 'width=270,height=95');
        }
    }
    
    fallbackToDirectWebSocket() {
        // SharedWorker 실패 시 직접 WebSocket 연결
        console.log('Fallback to direct WebSocket');
        const ws = new WebSocket('wss://fkdnaapi.futureirb.com/graphql');
        
        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);
            this.handleNewRequest(data);
        };
        
        // 재연결 로직
        ws.onclose = () => {
            setTimeout(() => this.fallbackToDirectWebSocket(), 5000);
        };
    }
    
    checkConnectionStatus() {
        // 연결 상태 확인 및 재연결
        if (this.sharedWorker) {
            this.sharedWorker.port.postMessage({ type: 'ping' });
        }
    }
    
    reconnectAll() {
        // 모든 연결 재시도
        this.initSharedWorker();
    }
}

// 자동 시작
const antiSleep = new AntiSleepSystem();

// 전역에서 접근 가능하도록
window.antiSleepSystem = antiSleep;

export default AntiSleepSystem;
